import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { SidebarContext } from '../../../context/SidebarContext';
import useCountUp from '../../../hooks/useCountUp';

/**
 * Generic donut + legend card.
 *
 * Segments carry their own colour so the same component serves the People
 * band (user states) and the feature planes (feature statuses).
 */
const DonutCard = ({ title, segments, centreLabel, loading, emptyLabel }) => {
  const { isDarkModeEnabled } = useContext(SidebarContext);

  const visible = (segments || []).filter((segment) => Number(segment.value) > 0);
  const total = (segments || []).reduce((sum, segment) => sum + (Number(segment.value) || 0), 0);
  const animatedTotal = useCountUp(total);
  const hasData = total > 0;

  const options = {
    chart: {
      type: 'donut',
      animations: { enabled: true, easing: 'easeinout', speed: 700 },
      toolbar: { show: false }
    },
    labels: visible.map((segment) => segment.label),
    colors: visible.map((segment) => segment.color),
    stroke: { width: 2, colors: [isDarkModeEnabled ? '#171717' : '#FFFFFF'] },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: { theme: isDarkModeEnabled ? 'dark' : 'light' },
    plotOptions: { pie: { donut: { size: '78%', labels: { show: false } } } },
    states: { hover: { filter: { type: 'lighten', value: 0.06 } } }
  };

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder rounded-2xl p-4 md:p-5 flex flex-col h-full">
      <h4 className="text-base font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg mb-2">
        {title}
      </h4>

      <div className="relative flex-1 flex items-center justify-center min-h-[168px]">
        {hasData && !loading ? (
          <>
            <Chart
              options={options}
              series={visible.map((segment) => Number(segment.value))}
              type="donut"
              height={180}
              width="100%"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[11px] text-shoorah-gray3">{centreLabel}</span>
              <span className="text-2xl font-semibold text-shoorah-lightBlack dark:text-shoorah-darkBg tabular-nums">
                {Math.round(animatedTotal).toLocaleString()}
              </span>
            </div>
          </>
        ) : (
          <span className="text-xs text-shoorah-gray3">{loading ? 'Loading…' : emptyLabel}</span>
        )}
      </div>

      <ul className="flex flex-col gap-[6px] mt-3">
        {(segments || []).map((segment) => (
          <li key={segment.label} className="flex items-center gap-2 text-xs">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: segment.color }}
            />
            <span className="text-shoorah-gray4 dark:text-shoorah-darkBg">{segment.label}:</span>
            <span className="font-semibold text-shoorah-lightBlack dark:text-shoorah-darkBg tabular-nums">
              {(Number(segment.value) || 0).toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

DonutCard.propTypes = {
  title: PropTypes.string.isRequired,
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number,
      color: PropTypes.string.isRequired
    })
  ),
  centreLabel: PropTypes.string,
  loading: PropTypes.bool,
  emptyLabel: PropTypes.string
};

DonutCard.defaultProps = {
  centreLabel: 'Total',
  emptyLabel: 'No data yet'
};

export default DonutCard;
